import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Button, Col, Row, Form, FormCheck } from 'react-bootstrap';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import {
    AuthedLayout,
    DateTimePicker,
    EditableProperties,
    EditableTableDiv,
    HeaderDisplayKeysWithType,
} from '../../../components';
import { useClassGrade, useSemGrade } from '../../../hook';
import { Sight, StudentGradeNoSeatBirth, WH } from '../../../model';
import apis from '../../../apis';
import { SightContext } from '../../TableHeaders/SightHeader';
import { downloadDataAsExcel, SheetHeaderDisplay } from '../../../utils';
import { whbmiDiag } from '../../../utils/sight';
import { Nullable } from '../../../types';
import { DateTime } from 'luxon';
import { WrapClassBaseLayout, ClassSem } from '../../../layouts';
import ReactDatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
type PageData = StudentGradeNoSeatBirth & Nullable<Sight> & Nullable<WH> & { diag: string } & EditableProperties & { fitbmistr: string } & { fitweightstr: string } & { agerange: number };

const boolRender = (value: boolean) => (
    <Form.Check type="checkbox" checked={value} readOnly />
);
const manageIDMap: Record<string, string> = {
    '1': '長效散瞳劑\n(阿托平 Atropine)',
    '2': '短效散瞳劑',
    '3': '其他藥物',
    '4': '配鏡矯治',
    '5': '更換鏡片',
    '6': '遮眼治療',
    '7': '配戴隱形眼鏡',
    '8': '角膜塑型片',
    '9': '視力保健衛教',
    '10': '其他',
    '11': '定期檢查',
};

enum ReceiptType {
    None = 0,
    Parent = 1,
    Treatment = 2
}

const formatSightValue = (value: unknown) => {
    if (typeof value !== 'number') return value as string;

    if (value === -1) return '<0.1';
    if (value < -1) return String(value);

    return Number.isInteger(value) ? value.toFixed(1) : String(value);
};
const tableHeader: (SheetHeaderDisplay<PageData> &
    HeaderDisplayKeysWithType<PageData>)[] = [
        { display: '座號', property: 'seat' },
        { display: '姓名', property: 'name' },
        // { display: '性別', property: 'sex' },
        { display: '身高', property: 'height' },
        { display: '體重', property: 'weight' },
        {
            display: '測量日',
            property: 'examDate',
            onRender: (v) => {
                if (!v) return "";
                if (v instanceof DateTime) {
                    const formatted = v.toFormat('yyyy/MM/dd');
                    return formatted === "0001/01/01" ? "" : formatted;
                }
                return v;
            },
            style: { width: '7%' }
        },
        { display: '體位', property: 'diag', style: { width: '5%' } },
        { display: '身高不足標準', property: 'fitHeight', style: { width: '5%' } },
        {
            display: 'BMI',
            property: 'bmi',
            onRender: (value: unknown) => {
                if (typeof value === 'number') {
                    return Math.round(value * 10) / 10;
                }
                return value;
            },
            onSheetRender: (value: unknown) => {
                if (typeof value === 'number') {
                    return Math.round(value * 10) / 10;
                }
                return value as string;
            },
        },
        {
            display: '實歲',
            property: 'agerange',
        },
        { display: '建議BMI', property: 'fitbmistr', style: { width: '5%' } },
        { display: '建議體重', property: 'fitweightstr', style: { width: '5%' } },
        {
            display: '裸視右',
            property: 'sight0R',
            onRender: (value) => formatSightValue(value),
            onSheetRender: (value) => formatSightValue(value),
        },
        {
            display: '裸視左',
            property: 'sight0L',
            onRender: (value) => formatSightValue(value),
            onSheetRender: (value) => formatSightValue(value),
        },
        {
            display: '戴鏡右',
            property: 'sightR',
            onRender: (value) => formatSightValue(value),
            onSheetRender: (value) => formatSightValue(value),
        },
        {
            display: '戴鏡左',
            property: 'sightL',
            onRender: (value) => formatSightValue(value),
            onSheetRender: (value) => formatSightValue(value),
        },
        { display: '近視', property: 'eNear', onRender: boolRender },
        { display: '遠視', property: 'eFar', onRender: boolRender },
        { display: '散光', property: 'eSan', onRender: boolRender },
        { display: '弱視', property: 'eWeak', onRender: boolRender },
        { display: '其他', property: 'eSight99', onRender: boolRender },
        { display: '複檢無異狀', property: 'noProblem', onRender: boolRender },
        { display: '備註', property: 'eSight99State' },
        {
            display: '處置代號',
            property: 'sightManageCodes',
            onRender: (v) => (Array.isArray(v) ? v.join(',') : v),
        },
        {
            display: '醫師建議處置',
            property: 'sightManageCodes',
            onRender: (v) => {
                if (Array.isArray(v)) {
                    return v.map(id => manageIDMap[id] || '').join(', ');
                }
                return '';
            },
            onSheetRender: (v) => {
                if (Array.isArray(v)) {
                    return v.map(id => manageIDMap[id] || '').join(', ');
                }
                return '';
            },
        },
    ];

const mapStates = (state: ApplicationState, ownProps: ClassSem) => ({
    ...ownProps,
    ...state.auth,
});
const mapDispatch = ErrorDispatches;

const connector = connect(mapStates, mapDispatch);
type Props = ConnectedProps<typeof connector>;

const wHSightClassList: FunctionComponent<Props> = ({
    classId,
    className,
    year,
    sem,
    grade,
    no,
    catchErrorForModal,
}) => {
    const { yearSem, element: semGradeElement } = useSemGrade();
    const { selectedClassGrade: classGrade, element: classGradeElement } =
        useClassGrade({
            yearSem
        });
    const [originalStudents, setOriginalStudents] = useState<PageData[]>([]);
    // const [NeedReceipt, setNeedReceipt] = useState<boolean>(true);
    // const [needReceiptForSightNormalBmiAbnormal, setNeedReceiptForSightNormalBmiAbnormal] = useState<boolean>(true); // 視力正常 + 體位異常
    // const [needReceiptForSightNormalBmiNormal, setNeedReceiptForSightNormalBmiNormal] = useState<boolean>(true);     // 視力正常 + 體位正常
    const [NeedSeparateReceipt, setNeedSeparateReceipt] = useState<boolean>(false);
    const [NeedPrintCurrentSight, setNeedPrintCurrentSight] = useState<boolean>(false);
    const [NeedReceiptDeadline, setNeedReceiptDeadline] = useState<boolean>(true);
    const [isPrintFitWeight, setIsPrintFitWeight] = useState<boolean>(false);
    const [wHSightClassListPdfDate, setWHSightClassListPdfDate] = useState<DateTime>();

    const [showAdvancedSetting, setShowAdvancedSetting] = useState(false);

    const [sightNormalBmiAbnormal, setSightNormalBmiAbnormal] = useState<'parent' | 'none'>('parent');

    const [sightNormalBmiNormal, setSightNormalBmiNormal] = useState<'parent' | 'none'>('parent');

    const [sightAbnormalReviewedBmiNormal, setSightAbnormalReviewedBmiNormal] = useState<'treat' | 'parent' | 'none'>('treat');

    const [sightAbnormalReviewedBmiAbnormal, setSightAbnormalReviewedBmiAbnormal] = useState<'treat' | 'parent' | 'none'>('treat');

    const receiptSettings = useMemo(() => {
        return {
            sightNormalBmiNormal:
                sightNormalBmiNormal === 'parent'
                    ? ReceiptType.Parent
                    : ReceiptType.None,

            sightNormalBmiAbnormal:
                sightNormalBmiAbnormal === 'parent'
                    ? ReceiptType.Parent
                    : ReceiptType.None,

            sightAbnormalBmiNormal:
                sightAbnormalReviewedBmiNormal === 'treat'
                    ? ReceiptType.Treatment
                    : sightAbnormalReviewedBmiNormal === 'parent'
                        ? ReceiptType.Parent
                        : ReceiptType.None,

            sightAbnormalBmiAbnormal:
                sightAbnormalReviewedBmiAbnormal === 'treat'
                    ? ReceiptType.Treatment
                    : sightAbnormalReviewedBmiAbnormal === 'parent'
                        ? ReceiptType.Parent
                        : ReceiptType.None,
        };
    }, [sightNormalBmiNormal, sightNormalBmiAbnormal, sightAbnormalReviewedBmiNormal, sightAbnormalReviewedBmiAbnormal,]);

    // const [showAdvancedReceiptSetting, setShowAdvancedReceiptSetting] = useState(false);

    // const [needReceiptForSightNormalPositionAbnormal, setNeedReceiptForSightNormalPositionAbnormal] = useState(true);
    // const [needReceiptForSightNormalPositionNormal, setNeedReceiptForSightNormalPositionNormal] = useState(true);

    // const [needReceiptForSightAbnormalPositionNormal, setNeedReceiptForSightAbnormalPositionNormal] = useState(false);
    // const [needReceiptForSightAbnormalPositionAbnormal, setNeedReceiptForSightAbnormalPositionAbnormal] = useState(false);

    const [bmiselectedFilter, setbmiSelectedFilter] = useState("體位全部");
    const [sightselectedFilter, setsightselectedFilter] = useState("視力全部");
    const [sightReviewFilter, setSightReviewFilter] = useState<string | null>(null);

    const [selectedIndex, setSelectedIndex] = useState<Set<number>>(
        new Set<number>()
    );
    const [pageName, setPageName] = useState('settings');

    function refreshSettings() {
        if (year && classId) {
            toast
                .promise(
                    apis
                        .getWHClassSight(year, sem, classId)
                        .then(r => {
                            const students = r.map(({ sight, wh, ...s }) => ({
                                ...sight,
                                ...wh,
                                ...s,
                                diag: whbmiDiag(wh?.bmiCode?.toString()),
                                fitbmistr: (wh?.bmiCode ?? "-1") != "0" ? (s.fitBMI ?? 0).toString() : "",
                                fitweightstr: (wh?.bmiCode ?? "-1") != "0" ? (s.fitWeight ?? 0).toString() : "",
                                agerange: calculateAge(s.birth, wh?.examDate ?? DateTime.now())
                            }));
                            setOriginalStudents(students);
                        }),
                    {
                        pending: '資料載入中......',
                        success: '資料載入成功！',
                        error: '資料載入失敗！請查看錯誤資訊。',
                    }
                )
                .catch(catchErrorForModal);
        }
    }

    function filterBmicode(value: string, data: PageData[]): PageData[] {
        const filterbmicode: string[] = [];
        switch (value) {
            case '體位正常':
                filterbmicode.push("0");
                break;
            case '體位異常':
                filterbmicode.push("-1");
                filterbmicode.push("1");
                filterbmicode.push("2");
                break;
            default:
                return data; // Always return something, even if it's the original list
        }
        return data.filter((s) => filterbmicode.includes(s.bmiCode ?? ""));
    }

    function filterSight(value: string, reviewFilter: string | null, data: PageData[]): PageData[] {
        let filtered = [...data];

        switch (value) {
            case '視力正常':
                filtered = filtered.filter((s) => (s.sight0R ?? 0) >= 0.9 && (s.sight0L ?? 0) >= 0.9);
                break;
            case '視力異常':
                filtered = filtered.filter((s) => (s.sight0R ?? 0) < 0.9 || (s.sight0L ?? 0) < 0.9);
                if (reviewFilter) {
                    filtered = filtered.filter((s) =>
                        reviewFilter === '已複檢' ? s.noProblem === true : s.noProblem === false
                    );
                }
                break;
            default:
                break;
        }

        return filtered;
    }

    useEffect(() => {
        refreshSettings()
    }, [classId, year, sem]);

    const students = useMemo(() => {
        const sightFiltered = filterSight(sightselectedFilter, sightReviewFilter, originalStudents);
        const bmiFiltered = filterBmicode(bmiselectedFilter, originalStudents);

        const newst = originalStudents.filter(s =>
            sightFiltered.includes(s) || bmiFiltered.includes(s)
        );

        return newst.map((s, i) => ({
            ...s,
            selected: selectedIndex.has(i),
        }));
    }, [originalStudents, bmiselectedFilter, sightselectedFilter, sightReviewFilter, selectedIndex]);

    if (!classId) return <></>;
    return (
        <>
            <Row className="align-items-start" style={{ margin: '5px' }}>
                {/* 資料查詢 Section */}
                <Col sm={12}>
                    <Row className='mb-1'>
                        <Col sm={12} className='border-bottom pt-0'>
                            <h3>資料查詢</h3>
                        </Col>
                    </Row>

                    <Row>
                        視力：
                        <Col sm={1} className='ps-0'>
                            <Form.Check
                                type="radio"
                                label="全部"
                                name="sight"
                                defaultChecked
                                onChange={(e) => {
                                    setsightselectedFilter("視力全部");
                                    setSightReviewFilter(null);
                                }}
                            />
                        </Col>
                        <Col sm={1} className='ps-0'>
                            <Form.Check
                                type="radio"
                                label="正常"
                                name="sight"
                                onChange={(e) => {
                                    setsightselectedFilter("視力正常");
                                    setSightReviewFilter(null);
                                }}
                            />
                        </Col>
                        <Col sm={1} className='ps-0'>
                            <Form.Check
                                type="radio"
                                label="異常"
                                name="sight"
                                onChange={(e) => {
                                    setsightselectedFilter("視力異常");
                                }}
                            />
                        </Col>

                        <Col sm={4} className='ps-0 d-inline-flex'>
                            （
                            <Form.Check
                                type="radio"
                                label="已複檢"
                                name="review"
                                disabled={sightselectedFilter !== "視力異常"}
                                onChange={() => setSightReviewFilter("已複檢")}
                            />

                            <Form.Check
                                type="radio"
                                label="未複檢"
                                name="review"
                                disabled={sightselectedFilter !== "視力異常"}
                                onChange={() => setSightReviewFilter("未複檢")}
                            />
                            ）
                        </Col>
                    </Row>
                    <Row>
                        體位：
                        <Col sm={1} className='ps-0'>
                            <Form.Check
                                type="radio"
                                label="全部"
                                name="wh"
                                defaultChecked
                                onChange={(e) => {
                                    setbmiSelectedFilter("體位全部");
                                }}
                            />
                        </Col>
                        <Col sm={1} className='ps-0'>
                            <Form.Check
                                type="radio"
                                label="正常"
                                name="wh"
                                onChange={(e) => {
                                    setbmiSelectedFilter("體位正常");
                                }}
                            />
                        </Col>
                        <Col sm={1} className='ps-0'>
                            <Form.Check
                                type="radio"
                                label="異常"
                                name="wh"
                                onChange={(e) => {
                                    setbmiSelectedFilter("體位異常");
                                }}
                            />
                        </Col>
                    </Row>
                </Col>



                {/* 通知單設定 Section placed below 資料查詢 */}
                <Col sm={12} style={{ marginTop: '20px' }}>
                    <Row className='mb-1'>
                        <Col sm={12} className='border-bottom pt-0'>
                            <h3>通知單設定</h3>
                        </Col>
                    </Row>
                    <Row className='mb-1'>
                        <Col sm={12} className='border-bottom mb-1'>
                            <h5>衛教內容設定</h5>
                        </Col>
                        <Col sm={12} className='mb-1'>
                            <Form.Label>列印視力值：</Form.Label>
                            <Form.Check
                                inline
                                type="radio"
                                label="是"
                                name="education"
                                onChange={(e) => {
                                    setNeedPrintCurrentSight(true);
                                }}
                            />
                            <Form.Check
                                inline
                                type="radio"
                                label="否"
                                name="education"
                                defaultChecked
                                onChange={(e) => {
                                    setNeedPrintCurrentSight(false);
                                }}
                            />
                        </Col>
                        <Col sm={12}>
                            <Form.Label>列印建議體重：</Form.Label>
                            <Form.Check
                                inline
                                type="radio"
                                label="是"
                                name="fitweight"
                                onChange={(e) => {
                                    setIsPrintFitWeight(true);
                                }}
                            />
                            <Form.Check
                                inline
                                type="radio"
                                label="否"
                                name="fitweight"
                                defaultChecked
                                onChange={(e) => {
                                    setIsPrintFitWeight(false);
                                }}
                            />
                        </Col>
                    </Row>
                    <Row className='mb-1'>
                        <Col sm={12} className='border-bottom mb-1'>
                            <h5>回條內容設定</h5>
                        </Col>

                        <Col sm={12} className='mb-1'>
                            <div className='mb-3'>
                                <span className='mr-3'>
                                    視力正常，且體位異常者
                                </span>

                                <FormCheck
                                    inline
                                    type='radio'
                                    label='列印家長回條'
                                    checked={sightNormalBmiAbnormal === 'parent'}
                                    onChange={() =>
                                        setSightNormalBmiAbnormal('parent')
                                    }
                                />

                                <FormCheck
                                    inline
                                    type='radio'
                                    label='無回條單'
                                    checked={sightNormalBmiAbnormal === 'none'}
                                    onChange={() =>
                                        setSightNormalBmiAbnormal('none')
                                    }
                                />
                            </div>

                            <div className='mb-3'>
                                <span className='mr-3'>
                                    視力正常，且體位正常者
                                </span>

                                <FormCheck
                                    inline
                                    type='radio'
                                    label='列印家長回條'
                                    checked={sightNormalBmiNormal === 'parent'}
                                    onChange={() =>
                                        setSightNormalBmiNormal('parent')
                                    }
                                />

                                <FormCheck
                                    inline
                                    type='radio'
                                    label='無回條單'
                                    checked={sightNormalBmiNormal === 'none'}
                                    onChange={() =>
                                        setSightNormalBmiNormal('none')
                                    }
                                />
                            </div>

                            <div>
                                <span>
                                    視力異常者，列印矯治回條
                                </span>

                                <Button
                                    variant='link'
                                    className='p-0 ml-3'
                                    onClick={() =>
                                        setShowAdvancedSetting(
                                            !showAdvancedSetting
                                        )
                                    }
                                >
                                    {showAdvancedSetting
                                        ? '<<<關閉進階設定'
                                        : '已複檢者進階設定>>>'}
                                </Button>
                            </div>

                            {showAdvancedSetting && (
                                <div
                                    style={{
                                        marginTop: '12px',
                                        borderTop: '1px solid #ccc',
                                        paddingTop: '12px',
                                    }}
                                >
                                    <div className='mb-3'>
                                        <span className='mr-3'>
                                            體位正常者
                                        </span>

                                        <FormCheck
                                            inline
                                            type='radio'
                                            label='列印矯治回條'
                                            checked={
                                                sightAbnormalReviewedBmiNormal ===
                                                'treat'
                                            }
                                            onChange={() =>
                                                setSightAbnormalReviewedBmiNormal(
                                                    'treat'
                                                )
                                            }
                                        />

                                        <FormCheck
                                            inline
                                            type='radio'
                                            label='列印家長回條'
                                            checked={
                                                sightAbnormalReviewedBmiNormal ===
                                                'parent'
                                            }
                                            onChange={() =>
                                                setSightAbnormalReviewedBmiNormal(
                                                    'parent'
                                                )
                                            }
                                        />

                                        <FormCheck
                                            inline
                                            type='radio'
                                            label='無回條單'
                                            checked={
                                                sightAbnormalReviewedBmiNormal ===
                                                'none'
                                            }
                                            onChange={() =>
                                                setSightAbnormalReviewedBmiNormal(
                                                    'none'
                                                )
                                            }
                                        />
                                    </div>

                                    <div>
                                        <span className='mr-3'>
                                            體位異常者
                                        </span>

                                        <FormCheck
                                            inline
                                            type='radio'
                                            label='列印矯治回條'
                                            checked={
                                                sightAbnormalReviewedBmiAbnormal ===
                                                'treat'
                                            }
                                            onChange={() =>
                                                setSightAbnormalReviewedBmiAbnormal(
                                                    'treat'
                                                )
                                            }
                                        />

                                        <FormCheck
                                            inline
                                            type='radio'
                                            label='列印家長回條'
                                            checked={
                                                sightAbnormalReviewedBmiAbnormal ===
                                                'parent'
                                            }
                                            onChange={() =>
                                                setSightAbnormalReviewedBmiAbnormal(
                                                    'parent'
                                                )
                                            }
                                        />

                                        <FormCheck
                                            inline
                                            type='radio'
                                            label='無回條單'
                                            checked={
                                                sightAbnormalReviewedBmiAbnormal ===
                                                'none'
                                            }
                                            onChange={() =>
                                                setSightAbnormalReviewedBmiAbnormal(
                                                    'none'
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                            )}
                        </Col>
                    </Row>
                    <Row className='mb-1'>
                        <Col sm={12} className='border-bottom mb-1'>
                            <h5>繳回期限設定</h5>
                        </Col>
                        <Col sm={12} className='mb-1'>
                            <Row className='mb-1'>
                                <Col sm={4} className='mb-1' style={{ marginLeft: '15px' }} >
                                    <Form.Label>列印繳回期限：</Form.Label>
                                    <Form.Check
                                        inline
                                        type="radio"
                                        label="是"
                                        name="print_date"
                                        defaultChecked
                                        onChange={(e) => {
                                            setNeedReceiptDeadline(true);
                                        }}
                                    />
                                    <Form.Check
                                        inline
                                        type="radio"
                                        label="否"
                                        name="print_date"
                                        onChange={(e) => {
                                            setNeedReceiptDeadline(false);
                                        }}
                                    />
                                </Col>
                                <Col sm={4} >
                                    繳回期限：（ex.1999/01/01）
                                    <div style={{ flex: '0 0 25%' }} className="mr-2">
                                        <DateTimePicker
                                            containerClass="px-2 py-1"
                                            value={wHSightClassListPdfDate}
                                            onChange={(date) => {
                                                setWHSightClassListPdfDate(date);
                                            }}
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <Row className='mb-1'>
                        <Col sm={12} className='border-bottom mb-1'>
                            <h5>通知單格式設定</h5>
                        </Col>
                        <Col sm={12}>
                            <Form.Label>衛教內容與回條單分開列印：</Form.Label>
                            <Form.Check
                                inline
                                type="radio"
                                label="是"
                                name="print_separate"
                                onChange={(e) => {
                                    setNeedSeparateReceipt(true);
                                }}
                            />
                            <Form.Check
                                inline
                                type="radio"
                                label="否"
                                name="print_separate"
                                defaultChecked
                                onChange={(e) => {
                                    setNeedSeparateReceipt(false);
                                }}
                            />
                        </Col>
                    </Row>
                </Col >
            </Row >

            <Row>
                <Button
                    className="ml-auto mb-2"
                    disabled={!students.length}
                    onClick={() => {
                        let pids = students
                            .filter((s) => s.selected)
                            .map((s) => s.pid);

                        pids =
                            pids.length === 0
                                ? students.map((s) => s.pid)
                                : pids;

                        toast
                            .promise(
                                apis.downloadWHSightPDF(
                                    classId,
                                    pids,
                                    year,
                                    sem,
                                    receiptSettings,
                                    NeedPrintCurrentSight,
                                    NeedSeparateReceipt,
                                    NeedReceiptDeadline,
                                    isPrintFitWeight,
                                    wHSightClassListPdfDate
                                ),
                                {
                                    pending: '通知單產生中...',
                                    success: '通知單產生成功！',
                                    error: '通知單產生失敗！請查看錯誤資訊。',
                                }
                            )
                            .then((blob) => {
                                const pdfBlob =
                                    blob instanceof Blob
                                        ? blob.slice(
                                            0,
                                            blob.size,
                                            'application/pdf'
                                        )
                                        : new Blob(
                                            [blob],
                                            {
                                                type: 'application/pdf',
                                            }
                                        );

                                const pdfUrl =
                                    window.URL.createObjectURL(
                                        pdfBlob
                                    );

                                const pdfWindow =
                                    window.open(
                                        pdfUrl,
                                        '_blank'
                                    );

                                if (!pdfWindow) {
                                    toast.error(
                                        '瀏覽器阻擋了 PDF 視窗，請允許彈出式視窗。'
                                    );

                                    window.URL.revokeObjectURL(
                                        pdfUrl
                                    );

                                    return;
                                }

                                setTimeout(() => {
                                    window.URL.revokeObjectURL(
                                        pdfUrl
                                    );
                                }, 60000);
                            })
                            .catch(catchErrorForModal);
                    }}
                >
                    列印通知單
                </Button>

                <EditableTableDiv
                    headers={tableHeader}
                    values={students}
                    context={SightContext}
                    onSelected={(s) => {
                        setSelectedIndex((last) => {
                            if (s.index !== undefined) {
                                if (s.checked) {
                                    last.add(s.index);
                                } else {
                                    last.delete(s.index);
                                }

                                return new Set(last);
                            }

                            if (s.checked) {
                                return new Set(students.keys());
                            }

                            return new Set();
                        });
                    }}
                />
            </Row>
        </>
    );
};

function calculateAge(birthDate: DateTime, examDate: DateTime): number {
    const birth = new Date(birthDate.year, birthDate.month - 1, birthDate.day);
    const exam = new Date(examDate.year, examDate.month - 1, examDate.day);

    if (exam < birth) return 0;

    const totalDays = (exam.getTime() - birth.getTime()) / (1000 * 60 * 60 * 24);

    const age = Math.floor(totalDays / 365);

    return age < 0 ? 0 : age;
}


export const WHSightClassList = connector(WrapClassBaseLayout(wHSightClassList));